import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Stack,
  Button,
  IconButton,
  Tooltip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  CircularProgress
} from '@mui/material';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { useToast } from '../../context/ToastContext';
import {
  fetchEmployeeAbsences,
  createEmployeeAbsence,
  deleteEmployeeAbsence,
  fetchSingleStaff
} from '../../slices/staffSlice';

const ABSENCE_REASONS = [
  { value: 'sick', label: 'مرض' },
  { value: 'personal', label: 'أمر شخصي' },
  { value: 'emergency', label: 'طوارئ' },
  { value: 'unpaid_leave', label: 'إجازة بدون راتب' },
  { value: 'other', label: 'أخرى' }
];

const MONTH_NUMBERS = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0'));

const getReasonLabel = (reason) => ABSENCE_REASONS.find(r => r.value === reason)?.label || reason;

const getReasonColor = (reason) => {
  const colors = {
    sick: 'error',
    personal: 'warning',
    emergency: 'error',
    unpaid_leave: 'info',
    other: 'default'
  };
  return colors[reason] || 'default';
};

const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const EmployeeAbsenceSection = ({ employeeId }) => {
  const dispatch = useDispatch();
  const { showToast } = useToast();
  const { employeeAbsences, employeeAbsenceLoading } = useSelector((state) => state.staff);

  const now = new Date();
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  // 'all' shows every month in the selected year; otherwise 1-12
  const [selectedMonth, setSelectedMonth] = useState('all');
  const [openForm, setOpenForm] = useState(false);
  const [absenceDate, setAbsenceDate] = useState('');
  const [reason, setReason] = useState('other');
  const [feeAmount, setFeeAmount] = useState('');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (employeeId) {
      dispatch(fetchEmployeeAbsences({ employee: employeeId }));
    }
  }, [employeeId, dispatch]);

  const employeeAbsenceRecords = useMemo(
    () => employeeAbsences.filter(a => a.employee === employeeId),
    [employeeAbsences, employeeId]
  );

  const yearOptions = useMemo(() => {
    const years = new Set([now.getFullYear()]);
    employeeAbsenceRecords.forEach(a => {
      years.add(new Date(a.absence_date).getFullYear());
    });
    return Array.from(years).sort((a, b) => b - a);
  }, [employeeAbsenceRecords]); // eslint-disable-line react-hooks/exhaustive-deps

  const yearRecords = useMemo(() => {
    return employeeAbsenceRecords
      .filter(a => new Date(a.absence_date).getFullYear() === Number(selectedYear))
      .sort((a, b) => new Date(b.absence_date) - new Date(a.absence_date));
  }, [employeeAbsenceRecords, selectedYear]);

  const visibleRecords = useMemo(() => {
    if (selectedMonth === 'all') return yearRecords;
    return yearRecords.filter(a => new Date(a.absence_date).getMonth() + 1 === Number(selectedMonth));
  }, [yearRecords, selectedMonth]);

  const totalFees = visibleRecords.reduce((sum, a) => sum + Number(a.fee_amount || 0), 0);

  const resetForm = () => {
    setAbsenceDate('');
    setReason('other');
    setFeeAmount('');
    setNotes('');
  };

  const handleCreate = async () => {
    setIsSubmitting(true);
    try {
      await dispatch(createEmployeeAbsence({
        employee: employeeId,
        absence_date: absenceDate,
        reason,
        fee_amount: feeAmount ? Number(feeAmount) : 0,
        notes
      })).unwrap();

      showToast('تم تسجيل الغياب بنجاح', 'success');
      setOpenForm(false);
      resetForm();
      // Refresh staff financial data so any fee deduction reflects in the transactions table
      // for the month the absence actually belongs to; it will not show up in other months.
      dispatch(fetchSingleStaff(employeeId));
    } catch (error) {
      showToast(error?.absence_date?.[0] || error?.detail || 'فشل في تسجيل الغياب', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('هل أنت متأكد من حذف سجل الغياب هذا؟ سيتم حذف أي خصم مرتبط به.')) {
      try {
        await dispatch(deleteEmployeeAbsence(id)).unwrap();
        showToast('تم حذف سجل الغياب بنجاح', 'success');
        dispatch(fetchSingleStaff(employeeId));
      } catch (error) {
        showToast(error?.detail || 'فشل في حذف سجل الغياب', 'error');
      }
    }
  };

  const periodLabel = selectedMonth === 'all'
    ? `سنة ${selectedYear}`
    : `${selectedMonth}/${selectedYear}`;

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        p: { xs: 2, sm: 3 },
        mb: 3,
        bgcolor: '#fff',
        border: '1px solid #E5E7EB',
        boxShadow: '0 2px 12px rgba(67,56,202,0.07)'
      }}
    >
      <Stack
        useFlexGap
        direction={{ xs: 'column', md: 'row' }}
        alignItems={{ xs: 'stretch', md: 'center' }}
        justifyContent="space-between"
        spacing={2}
        sx={{ mb: 2 }}
      >
        <Stack useFlexGap direction="row" spacing={1.5} alignItems="center">
          <PersonOffIcon sx={{ color: '#DC2626' }} />
          <Typography sx={{ fontSize: '1.15rem', fontWeight: 800, color: '#1E1B4B', fontFamily: 'Cairo, Tajawal, sans-serif' }}>
            سجل الغياب
          </Typography>
          <Chip
            label={`${visibleRecords.length} يوم`}
            size="small"
            sx={{ fontWeight: 700, bgcolor: '#FEE2E2', color: '#DC2626', fontFamily: 'Cairo, Tajawal, sans-serif' }}
          />
        </Stack>

        <Stack useFlexGap direction="row" spacing={1.5} alignItems="center" sx={{ flexWrap: 'wrap' }}>
          <TextField
            label="السنة"
            select
            size="small"
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
            SelectProps={{ native: true }}
            sx={{ minWidth: 110, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          >
            {yearOptions.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </TextField>
          <TextField
            label="الشهر"
            select
            size="small"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            SelectProps={{ native: true }}
            sx={{ minWidth: 130, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          >
            <option value="all">كل الأشهر</option>
            {MONTH_NUMBERS.map((m, idx) => (
              <option key={m} value={idx + 1}>{m}</option>
            ))}
          </TextField>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpenForm(true)}
            sx={{
              borderRadius: 2.5,
              fontWeight: 700,
              textTransform: 'none',
              fontFamily: 'Cairo, Tajawal, sans-serif',
              bgcolor: '#DC2626',
              boxShadow: 'none',
              '&:hover': { bgcolor: '#B91C1C', boxShadow: 'none' },
            }}
          >
            تسجيل غياب
          </Button>
        </Stack>
      </Stack>

      {employeeAbsenceLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : visibleRecords.length === 0 ? (
        <Typography variant="body2" sx={{ color: '#6B7280', py: 2, textAlign: 'center', fontFamily: 'Cairo, Tajawal, sans-serif' }}>
          لا توجد أيام غياب مسجلة لهذا الموظف في {periodLabel}
        </Typography>
      ) : (
        <TableContainer sx={{ borderRadius: 3, overflowX: 'auto' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell align="center" sx={{ fontWeight: 700, color: '#1E1B4B', bgcolor: '#F1F5F9', fontFamily: 'Cairo, Tajawal, sans-serif' }}>التاريخ</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700, color: '#1E1B4B', bgcolor: '#F1F5F9', fontFamily: 'Cairo, Tajawal, sans-serif' }}>سبب الغياب</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700, color: '#1E1B4B', bgcolor: '#F1F5F9', fontFamily: 'Cairo, Tajawal, sans-serif' }}>ملاحظات</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700, color: '#DC2626', bgcolor: '#F1F5F9', fontFamily: 'Cairo, Tajawal, sans-serif' }}>مبلغ الخصم (شيكل)</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700, color: '#1E1B4B', bgcolor: '#F1F5F9', fontFamily: 'Cairo, Tajawal, sans-serif' }}>إجراءات</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleRecords.map((absence) => (
                <TableRow key={absence.id} hover sx={{ '& .MuiTableCell-root': { fontFamily: 'Cairo, Tajawal, sans-serif' } }}>
                  <TableCell align="center">
                    <Stack useFlexGap direction="row" spacing={1} alignItems="center" justifyContent="center">
                      <CalendarTodayIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Typography fontWeight={600}>{formatDate(absence.absence_date)}</Typography>
                    </Stack>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={absence.reason_display || getReasonLabel(absence.reason)}
                      color={getReasonColor(absence.reason)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" color="text.secondary">{absence.notes || '—'}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography sx={{ color: Number(absence.fee_amount) > 0 ? '#DC2626' : '#6B7280', fontWeight: 700 }}>
                      {Number(absence.fee_amount) > 0 ? Number(absence.fee_amount).toLocaleString('en-US') : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="حذف سجل الغياب">
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(absence.id)}
                        sx={{ color: '#DC2626', '&:hover': { bgcolor: '#FEE2E2', color: '#DC2626' } }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell colSpan={3} align="center" sx={{ fontWeight: 700, color: '#1E1B4B', bgcolor: '#F1F5F9', fontFamily: 'Cairo, Tajawal, sans-serif' }}>
                  إجمالي الخصومات - {periodLabel}
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 800, color: '#DC2626', bgcolor: '#F1F5F9', fontFamily: 'Cairo, Tajawal, sans-serif' }}>
                  {totalFees.toLocaleString('en-US')}
                </TableCell>
                <TableCell sx={{ bgcolor: '#F1F5F9' }} />
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add Absence Dialog */}
      <Dialog
        open={openForm}
        onClose={() => setOpenForm(false)}
        dir="rtl"
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, fontFamily: 'Cairo, Tajawal, sans-serif' } }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: '#DC2626', fontFamily: 'Cairo, Tajawal, sans-serif' }}>
          تسجيل يوم غياب
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={2}>
            <TextField
              label="تاريخ الغياب"
              type="date"
              value={absenceDate}
              onChange={(e) => setAbsenceDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="سبب الغياب"
              select
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              SelectProps={{ native: true }}
            >
              {ABSENCE_REASONS.map((r) => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </TextField>
            <TextField
              label="مبلغ الخصم من الراتب (اختياري)"
              type="number"
              value={feeAmount}
              onChange={(e) => setFeeAmount(e.target.value)}
              helperText="عند إدخال مبلغ، سيتم خصمه تلقائيًا من راتب شهر تاريخ الغياب فقط، دون التأثير على أي شهر آخر"
              inputProps={{ min: 0, step: '0.01' }}
            />
            <TextField
              label="ملاحظات"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              multiline
              rows={2}
            />
            <Button
              variant="contained"
              disabled={isSubmitting || !absenceDate}
              onClick={handleCreate}
              sx={{
                borderRadius: 2.5,
                fontWeight: 700,
                textTransform: 'none',
                fontFamily: 'Cairo, Tajawal, sans-serif',
                bgcolor: '#DC2626',
                boxShadow: 'none',
                '&:hover': { bgcolor: '#B91C1C', boxShadow: 'none' },
              }}
            >
              {isSubmitting ? 'جاري الحفظ...' : 'تسجيل الغياب'}
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>
    </Paper>
  );
};

export default EmployeeAbsenceSection;
